import React from 'react';
import { label as labelPreset, fontWeight } from '../Constants/Fonts';

/**
 * CommonStepper — a reusable horizontal progress stepper themed with the global
 * light tokens (src/styles/theme.css) + Fonts.js presets. Self-contained (no
 * external CSS): renders numbered dots joined by connector lines, with state
 * colouring by convention (Atlassian/ServiceNow): past = success green,
 * current = brand blue, future = neutral grey. Past steps show a check.
 *
 * Props:
 *   steps        — [{ id, label }] in order
 *   currentIndex — index of the active step (past = < it, future = > it)
 *   current      — alternatively, the active step's id (resolved to an index)
 *   onStepClick(step, index) — optional; makes dots/labels clickable
 *
 * Example:
 *   <CommonStepper
 *      steps={[{id:'identify',label:'Identify'}, {id:'assess',label:'Assess'}, …]}
 *      current="assess" />
 */

const DONE = 'var(--color-success)';
const CURRENT = 'var(--color-primary)';
const FUTURE = 'var(--color-border-strong)';
const DOT = 30;

const CheckIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

export default function CommonStepper({ steps = [], currentIndex, current, onStepClick }) {
    const curr = typeof currentIndex === 'number'
        ? currentIndex
        : Math.max(0, steps.findIndex((s) => s.id === current));

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', fontFamily: 'var(--font-family)' }}>
            {steps.map((step, i) => {
                const state = i < curr ? 'done' : i === curr ? 'current' : 'future';
                const dotBg = state === 'done' ? DONE : state === 'current' ? CURRENT : 'var(--color-surface)';
                const dotBorder = state === 'future' ? `2px solid ${FUTURE}` : 'none';
                const dotColor = state === 'future' ? 'var(--color-text-muted)' : 'var(--color-text-inverse)';
                const labelColor = state === 'done' ? DONE : state === 'current' ? CURRENT : 'var(--color-text-muted)';
                const clickable = typeof onStepClick === 'function';

                return (
                    <React.Fragment key={step.id ?? i}>
                        <div
                            onClick={clickable ? () => onStepClick(step, i) : undefined}
                            style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                flex: '0 0 auto', maxWidth: 120, gap: 8,
                                cursor: clickable ? 'pointer' : 'default',
                            }}
                        >
                            <div
                                style={{
                                    width: DOT, height: DOT, borderRadius: '50%',
                                    background: dotBg, border: dotBorder, color: dotColor,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 13, fontWeight: fontWeight.semibold, flexShrink: 0,
                                    boxShadow: state === 'current' ? '0 0 0 4px var(--color-primary-soft)' : 'none',
                                    transition: 'background .15s ease',
                                }}
                            >
                                {state === 'done' ? CheckIcon : (i + 1)}
                            </div>
                            <div
                                style={labelPreset({
                                    color: labelColor, fontWeight: state === 'current' ? fontWeight.semibold : fontWeight.medium,
                                    textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em',
                                    lineHeight: 1.3, whiteSpace: 'nowrap',
                                })}
                            >
                                {step.label}
                            </div>
                        </div>
                        {i < steps.length - 1 && (
                            <div
                                style={{
                                    flex: 1, height: 2, minWidth: 24,
                                    background: i < curr ? DONE : FUTURE,
                                    marginTop: DOT / 2 - 1,
                                    transition: 'background .15s ease',
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}
